import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { PROVIDER_SCHEMAS } from '@/lib/providers/schema'
import {
  useIntegrationTiers,
  useSyncTiers,
  useIntegrationTickets,
  useTicketStats,
  useSyncLogs,
  useSyncIntegration,
  useCreditTickets,
} from '@/lib/api/provider-queries'
import type { Integration, TicketsParams } from '@/lib/api/provider-types'
import { TierMappingTable } from './TierMappingTable'
import { X, RefreshCw, Loader2, ArrowLeft, ArrowRight, CreditCard, Search } from 'lucide-react'

interface IntegrationDetailProps {
  integration: Integration
  eventId: number
  onClose: () => void
}

type DetailTab = 'overview' | 'tiers' | 'tickets' | 'sync-logs'

export function IntegrationDetail({ integration, eventId, onClose }: IntegrationDetailProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('overview')
  const [ticketPage, setTicketPage] = useState(1)
  const [ticketSearch, setTicketSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [matchedFilter, setMatchedFilter] = useState<string>('')
  const [creditedFilter, setCreditedFilter] = useState<string>('')
  const [tierFilter, setTierFilter] = useState<string>('')

  const schema = PROVIDER_SCHEMAS[integration.provider_type]
  const { data: tiers, isLoading: tiersLoading } = useIntegrationTiers(integration.id)
  const syncTiersMutation = useSyncTiers()

  // Fetch all tickets (backend doesn't support filter query params)
  const allTicketsParams: TicketsParams = { page: 1, page_size: 500 }
  const { data: allTicketsData, isLoading: ticketsLoading } = useIntegrationTickets(integration.id, allTicketsParams)

  // Client-side filtering
  const filteredTickets = useMemo(() => {
    let tickets = allTicketsData?.results ?? []
    if (ticketSearch) {
      const q = ticketSearch.toLowerCase()
      tickets = tickets.filter(t =>
        t.attendee_first_name?.toLowerCase().includes(q) ||
        t.attendee_last_name?.toLowerCase().includes(q) ||
        t.attendee_email?.toLowerCase().includes(q) ||
        t.purchaser_email?.toLowerCase().includes(q) ||
        t.purchaser_first_name?.toLowerCase().includes(q) ||
        t.purchaser_last_name?.toLowerCase().includes(q)
      )
    }
    if (statusFilter) {
      tickets = tickets.filter(t => t.status === statusFilter)
    }
    if (matchedFilter) {
      const wantMatched = matchedFilter === 'true'
      tickets = tickets.filter(t => (t.matched_user != null) === wantMatched)
    }
    if (creditedFilter) {
      const wantCredited = creditedFilter === 'true'
      tickets = tickets.filter(t => t.tokens_credited === wantCredited)
    }
    if (tierFilter) {
      tickets = tickets.filter(t => t.tier_name === tierFilter)
    }
    return tickets
  }, [allTicketsData, ticketSearch, statusFilter, matchedFilter, creditedFilter, tierFilter])

  // Paginate client-side
  const pageSize = 20
  const totalFiltered = filteredTickets.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize))
  const paginatedTickets = filteredTickets.slice((ticketPage - 1) * pageSize, ticketPage * pageSize)

  const matchedUncreditedTickets = useMemo(
    () => (allTicketsData?.results ?? []).filter(t => t.matched_user != null && !t.tokens_credited),
    [allTicketsData]
  )
  const { data: stats } = useTicketStats(integration.id)
  const { data: syncLogsData, isLoading: logsLoading } = useSyncLogs(integration.id)
  const syncMutation = useSyncIntegration()
  const creditMutation = useCreditTickets()

  const handleCreditTickets = () => {
    const ticketIds = matchedUncreditedTickets.map(t => t.id)
    if (ticketIds.length === 0) return
    creditMutation.mutate({ integrationId: integration.id, ticketIds })
  }

  const tabs: { key: DetailTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'tiers', label: 'Tiers' },
    { key: 'tickets', label: 'Tickets' },
    { key: 'sync-logs', label: 'Sync Logs' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white dark:bg-neutral-950 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              {schema?.label} Integration
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {integration.external_event_name}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200 dark:border-neutral-800 px-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Total Tickets', value: stats.total },
                    { label: 'Matched', value: stats.matched },
                    { label: 'Unmatched', value: stats.unmatched },
                    { label: 'Credited', value: stats.credited },
                  ].map(stat => (
                    <Card key={stat.label}>
                      <CardContent className="pt-4 pb-4">
                        <div className="text-2xl font-bold text-neutral-900 dark:text-white">{stat.value}</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">{stat.label}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">Status</span>
                      <span className="font-medium text-neutral-900 dark:text-white capitalize">{integration.sync_status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">Last Synced</span>
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {integration.last_synced_at ? new Date(integration.last_synced_at).toLocaleString() : 'Never'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">External Event ID</span>
                      <span className="font-medium text-neutral-900 dark:text-white">{integration.external_event_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">Enabled</span>
                      <span className="font-medium text-neutral-900 dark:text-white">{integration.is_enabled ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => syncMutation.mutate(integration.id)}
                  disabled={syncMutation.isPending || integration.sync_status === 'syncing'}
                >
                  {syncMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                  Sync Now
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCreditTickets}
                  disabled={creditMutation.isPending || matchedUncreditedTickets.length === 0}
                >
                  {creditMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />}
                  Credit Matched Tickets{matchedUncreditedTickets.length > 0 ? ` (${matchedUncreditedTickets.length})` : ''}
                </Button>
              </div>
            </div>
          )}

          {/* Tiers Tab */}
          {activeTab === 'tiers' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Map each provider tier to token amounts and vouchers.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => syncTiersMutation.mutate(integration.id)}
                  disabled={syncTiersMutation.isPending}
                >
                  {syncTiersMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                  Re-sync Tiers
                </Button>
              </div>
              {tiersLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
              ) : (
                <TierMappingTable integrationId={integration.id} tiers={tiers ?? []} eventId={eventId} />
              )}
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                  <Input
                    type="text"
                    placeholder="Search by name or email..."
                    value={ticketSearch}
                    onChange={e => { setTicketSearch(e.target.value); setTicketPage(1) }}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setTicketPage(1) }}
                  className="h-8 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm px-2 text-neutral-700 dark:text-neutral-300"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refunded">Refunded</option>
                </select>
                <select
                  value={matchedFilter}
                  onChange={e => { setMatchedFilter(e.target.value); setTicketPage(1) }}
                  className="h-8 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm px-2 text-neutral-700 dark:text-neutral-300"
                >
                  <option value="">All Matched</option>
                  <option value="true">Matched</option>
                  <option value="false">Unmatched</option>
                </select>
                <select
                  value={creditedFilter}
                  onChange={e => { setCreditedFilter(e.target.value); setTicketPage(1) }}
                  className="h-8 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm px-2 text-neutral-700 dark:text-neutral-300"
                >
                  <option value="">All Credited</option>
                  <option value="true">Credited</option>
                  <option value="false">Not Credited</option>
                </select>
                {tiers && tiers.length > 0 && (
                  <select
                    value={tierFilter}
                    onChange={e => { setTierFilter(e.target.value); setTicketPage(1) }}
                    className="h-8 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm px-2 text-neutral-700 dark:text-neutral-300"
                  >
                    <option value="">All Tiers</option>
                    {tiers.map(t => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {ticketsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
              ) : paginatedTickets.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-200 dark:border-neutral-800">
                          <th className="text-left py-2 px-2 font-medium text-neutral-600 dark:text-neutral-400">Attendee</th>
                          <th className="text-left py-2 px-2 font-medium text-neutral-600 dark:text-neutral-400">Tier</th>
                          <th className="text-left py-2 px-2 font-medium text-neutral-600 dark:text-neutral-400">Status</th>
                          <th className="text-left py-2 px-2 font-medium text-neutral-600 dark:text-neutral-400">Matched</th>
                          <th className="text-left py-2 px-2 font-medium text-neutral-600 dark:text-neutral-400">Credited</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTickets.map(ticket => {
                          const name = [ticket.attendee_first_name, ticket.attendee_last_name].filter(Boolean).join(' ') || 'Unknown'
                          const isMatched = ticket.matched_user != null
                          return (
                            <tr key={ticket.id} className="border-b border-neutral-100 dark:border-neutral-800/50">
                              <td className="py-2 px-2">
                                <div className="text-neutral-900 dark:text-white">{name}</div>
                                <div className="text-xs text-neutral-500 dark:text-neutral-400">{ticket.attendee_email}</div>
                              </td>
                              <td className="py-2 px-2 text-neutral-600 dark:text-neutral-400">{ticket.tier_name || '-'}</td>
                              <td className="py-2 px-2">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                  ticket.status === 'active'
                                    ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : ticket.status === 'cancelled'
                                    ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
                                }`}>
                                  {ticket.status}
                                </span>
                              </td>
                              <td className="py-2 px-2">
                                <span className={isMatched ? 'text-green-600 dark:text-green-400' : 'text-neutral-400'}>
                                  {isMatched ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="py-2 px-2">
                                <span className={ticket.tokens_credited ? 'text-green-600 dark:text-green-400' : 'text-neutral-400'}>
                                  {ticket.tokens_credited ? 'Yes' : 'No'}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500">{totalFiltered} tickets</span>
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={ticketPage <= 1}
                          onClick={() => setTicketPage(p => Math.max(1, p - 1))}
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                        </Button>
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">Page {ticketPage} of {totalPages}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={ticketPage >= totalPages}
                          onClick={() => setTicketPage(p => p + 1)}
                        >
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 py-4 text-center">
                  {ticketSearch || statusFilter || matchedFilter || creditedFilter || tierFilter
                    ? 'No tickets match the current filters.'
                    : 'No tickets synced yet.'}
                </p>
              )}
            </div>
          )}

          {/* Sync Logs Tab */}
          {activeTab === 'sync-logs' && (
            <div className="space-y-4">
              {logsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
              ) : syncLogsData && syncLogsData.results.length > 0 ? (
                <div className="space-y-2">
                  {syncLogsData.results.map(log => (
                    <div
                      key={log.id}
                      className="p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium capitalize ${
                          log.status === 'completed' ? 'text-green-600 dark:text-green-400' :
                          log.status === 'failed' ? 'text-red-600 dark:text-red-400' :
                          'text-blue-600 dark:text-blue-400'
                        }`}>
                          {log.status}
                        </span>
                        <span className="text-neutral-500 dark:text-neutral-400">
                          {new Date(log.started_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-neutral-600 dark:text-neutral-400 mt-1">
                        {log.tickets_synced} tickets synced
                        {log.completed_at && (
                          <span className="ml-2">
                            ({Math.round((new Date(log.completed_at).getTime() - new Date(log.started_at).getTime()) / 1000)}s)
                          </span>
                        )}
                      </div>
                      {log.error && (
                        <div className="text-red-600 dark:text-red-400 mt-1">{log.error}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 py-4 text-center">No sync logs yet.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
